/**
 * Bioteiga Teich-Konfigurator
 * Custom Element: <pond-configurator>
 *
 * Data attributes:
 *   data-collection-url   – Base collection URL for redirect (e.g. /collections/alle-produkte)
 *   data-worker-endpoint  – Cloudflare Worker endpoint URL for customer API
 *   data-section-id       – Section ID for scoping
 */

/**
 * UI translations.
 *
 * Every user-facing string lives in the theme locale files under `bioteiga.configurator.*`
 * and is injected by snippets/bioteiga-configurator-i18n.liquid. That makes each one
 * translatable in Shopify's "Translate & Adapt" app (Theme content).
 *
 * Do NOT hardcode user-facing text here — add a key to locales/de.json + locales/en.default.json,
 * list it in the snippet, then reference it via bioteigaT('key', 'German fallback').
 * The fallback keeps the widget working if the snippet is ever missing.
 *
 * Placeholders use %{name} and are replaced from the optional `vars` object.
 */
function bioteigaT(key, fallback, vars) {
  var dict = (window.BIOTEIGA_I18N && window.BIOTEIGA_I18N.configurator) || {};
  var out = dict[key];
  if (out === undefined || out === null || out === '') out = fallback;
  if (vars) {
    Object.keys(vars).forEach(function(name) {
      out = out.split('%{' + name + '}').join(vars[name]);
    });
  }
  return out;
}

class PondConfigurator extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.selectedTags = [];
    this.pondData = {
      teichgroesse: null,
      fische: null,
      teichpflanzen: null,
      email: null
    };
  }

  connectedCallback() {
    this._cacheElements();
    this._initReturningCustomer();
    this._bindEvents();
    this._updateStepIndicator();
  }

  _cacheElements() {
    this.panels = this.querySelectorAll('[data-panel]');
    this.stepIndicators = this.querySelectorAll('.pond-configurator__step-indicator');
    this.optionCards = this.querySelectorAll('.pond-configurator__option-row');
    this.calcToggle = this.querySelector('[data-calc-toggle]');
    this.calcContainer = this.querySelector('[data-calculator]');
    this.calcLength = this.querySelector('[data-calc-length]');
    this.calcWidth = this.querySelector('[data-calc-width]');
    this.calcDepth = this.querySelector('[data-calc-depth]');
    this.calcRadius = this.querySelector('[data-calc-radius]');
    this.calcDepthRound = this.querySelector('[data-calc-depth-round]');
    this.calcResultEl = this.querySelector('[data-calc-result]');
    this.calcFormulaEl = this.querySelector('[data-calc-formula]');
    this.shapeButtons = this.querySelectorAll('[data-shape]');
    this.fieldsRectangular = this.querySelector('[data-calc-fields-rectangular]');
    this.fieldsRound = this.querySelector('[data-calc-fields-round]');
    this.selectedShape = 'rectangular';
    this.pondSizeInput = this.querySelector('[data-pond-size]');
    this.emailInput = this.querySelector('[data-email]');
    this.fishToggle = this.querySelector('[data-fish-toggle]');
    this.plantBtns = this.querySelectorAll('.pond-configurator__plant-btn');
    this.waterToggleBtn = this.querySelector('[data-toggle-water]');
    this.waterSection = this.querySelector('.pond-configurator__water-section');
    this.problemsSection = this.querySelector('[data-problems-section]');
    this.problemsToggleBtn = this.querySelector('[data-toggle-problems]');
    this.returningPanel = this.querySelector('[data-panel="returning"]');
  }

  _initReturningCustomer() {
    const email = this.dataset.customerEmail;
    const teichgroesse = this.dataset.customerTeichgroesse;

    this.isReturningCustomer = !!(email && teichgroesse && parseInt(teichgroesse, 10) > 0);
    this._editMode = false;

    // Cache both step indicators
    this.stepsDefault = this.querySelector('[data-steps="default"]');
    this.stepsReturning = this.querySelector('[data-steps="returning"]');
    this.returningStepIndicators = this.querySelectorAll('[data-step-r]');

    if (this.isReturningCustomer) {
      this.customerData = {
        email: email,
        teichgroesse: parseInt(teichgroesse, 10),
        fische: this.dataset.customerFische === 'true',
        teichpflanzen: this.dataset.customerTeichpflanzen || 'keine'
      };

      // Show 3-step indicator, hide 4-step indicator
      if (this.stepsDefault) this.stepsDefault.hidden = true;
      if (this.stepsReturning) this.stepsReturning.hidden = false;
    }
  }

  _bindEvents() {
    // Option card selection (multi-select)
    this.optionCards.forEach(card => {
      card.addEventListener('click', () => this._toggleOption(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._toggleOption(card);
        }
      });
    });

    // Water values toggle: show water, hide problems
    if (this.waterToggleBtn && this.waterSection && this.problemsSection) {
      this.waterToggleBtn.addEventListener('click', () => {
        this.problemsSection.hidden = true;
        this.waterToggleBtn.hidden = true;
        this.waterSection.hidden = false;
        if (this.problemsToggleBtn) this.problemsToggleBtn.hidden = false;
      });
    }

    // Back to problems toggle: show problems, hide water
    if (this.problemsToggleBtn && this.problemsSection) {
      this.problemsToggleBtn.addEventListener('click', () => {
        this.waterSection.hidden = true;
        this.problemsSection.hidden = false;
        this.problemsToggleBtn.hidden = true;
        if (this.waterToggleBtn) this.waterToggleBtn.hidden = false;
      });
    }

    // Plant buttons (single select)
    this.plantBtns.forEach(btn => {
      btn.addEventListener('click', () => this._selectPlant(btn));
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._selectPlant(btn);
        }
      });
    });

    // Navigation: Next buttons
    this.querySelectorAll('[data-action="next"]').forEach(btn => {
      btn.addEventListener('click', () => this._goToStep(this.currentStep + 1));
    });

    // Navigation: Back buttons
    this.querySelectorAll('[data-action="back"]').forEach(btn => {
      btn.addEventListener('click', () => this._goToStep(this.currentStep - 1));
    });

    // Returning customer: "Lösung anzeigen" (direct submit)
    this.querySelectorAll('[data-action="returning-submit"]').forEach(btn => {
      btn.addEventListener('click', () => this._returningSubmit());
    });

    // Returning customer: Edit button
    this.querySelectorAll('[data-action="edit-pond"]').forEach(btn => {
      btn.addEventListener('click', () => this._returningEdit());
    });

    // Returning customer: Back button (go back to step 1)
    this.querySelectorAll('[data-action="returning-back"]').forEach(btn => {
      btn.addEventListener('click', () => this._returningBack());
    });

    // Calculator toggle
    if (this.calcToggle) {
      this.calcToggle.addEventListener('click', () => this._toggleCalculator());
    }

    // Shape selector
    this.shapeButtons.forEach(btn => {
      btn.addEventListener('click', () => this._selectShape(btn.dataset.shape));
    });

    // Calculator auto-compute
    [this.calcLength, this.calcWidth, this.calcDepth, this.calcRadius, this.calcDepthRound].forEach(field => {
      if (field) {
        field.addEventListener('input', () => this._computeVolume());
      }
    });

    // Apply calculator result button
    const applyBtn = this.querySelector('[data-calc-apply]');
    if (applyBtn) {
      applyBtn.addEventListener('click', () => this._applyCalculation());
    }
  }

  // ---- Option Selection ----

  _toggleOption(card) {
    card.classList.toggle('is-selected');
    card.setAttribute('aria-pressed', card.classList.contains('is-selected') ? 'true' : 'false');
  }

  _selectPlant(btn) {
    this.plantBtns.forEach(b => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
  }

  // ---- Calculator ----

  _toggleCalculator() {
    if (!this.calcContainer) return;
    const isHidden = this.calcContainer.hidden;
    this.calcContainer.hidden = !isHidden;
    this.calcToggle.textContent = isHidden
      ? bioteigaT('calc_toggle_close', 'Direkt Literzahl eingeben')
      : bioteigaT('calc_toggle_open', 'Teichgr\u00f6\u00dfe berechnen');

    // Toggle the main input visibility
    const sizeWrapper = this.querySelector('[data-pond-size-wrapper]');
    if (sizeWrapper) {
      sizeWrapper.hidden = isHidden;
    }
  }

  _selectShape(shape) {
    this.selectedShape = shape;
    this.shapeButtons.forEach(btn => {
      btn.classList.toggle('is-selected', btn.dataset.shape === shape);
    });
    if (this.fieldsRectangular) this.fieldsRectangular.hidden = (shape !== 'rectangular');
    if (this.fieldsRound) this.fieldsRound.hidden = (shape !== 'round');
    if (this.calcFormulaEl) {
      this.calcFormulaEl.textContent = shape === 'round'
        ? bioteigaT('formula_round', 'Formel: Radius × Radius × 3,14 × Tiefe ÷ 1,5 × 1.000')
        : bioteigaT('formula_rect', 'Formel: Länge × Breite × Tiefe ÷ 1,5 × 1.000');
    }
    this._computeVolume();
  }

  _computeVolume() {
    let liters = 0;
    if (this.selectedShape === 'round') {
      const r = parseFloat(this.calcRadius?.value) || 0;
      const d = parseFloat(this.calcDepthRound?.value) || 0;
      liters = Math.round(r * r * 3.14 * d / 1.5 * 1000);
    } else {
      const l = parseFloat(this.calcLength?.value) || 0;
      const w = parseFloat(this.calcWidth?.value) || 0;
      const d = parseFloat(this.calcDepth?.value) || 0;
      liters = Math.round(l * w * d / 1.5 * 1000);
    }

    if (this.calcResultEl) {
      this.calcResultEl.textContent = liters > 0
        ? liters.toLocaleString('de-DE') + ' Liter'
        : '0 Liter';
    }
  }

  _applyCalculation() {
    let liters = 0;
    if (this.selectedShape === 'round') {
      const r = parseFloat(this.calcRadius?.value) || 0;
      const d = parseFloat(this.calcDepthRound?.value) || 0;
      liters = Math.round(r * r * 3.14 * d / 1.5 * 1000);
    } else {
      const l = parseFloat(this.calcLength?.value) || 0;
      const w = parseFloat(this.calcWidth?.value) || 0;
      const d = parseFloat(this.calcDepth?.value) || 0;
      liters = Math.round(l * w * d / 1.5 * 1000);
    }

    if (liters > 0 && this.pondSizeInput) {
      this.pondSizeInput.value = liters;
      // Trigger floating label (Impact theme uses placeholder=" " pattern)
      this.pondSizeInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.pondSizeInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Switch back to direct input view
      if (this.calcContainer) this.calcContainer.hidden = true;
      const sizeWrapper = this.querySelector('[data-pond-size-wrapper]');
      if (sizeWrapper) sizeWrapper.hidden = false;
      if (this.calcToggle) this.calcToggle.textContent = bioteigaT('calc_toggle_open', 'Teichgr\u00f6\u00dfe berechnen');
    }
  }

  // ---- Step Navigation ----

  _goToStep(step) {
    // Validate before advancing
    if (step > this.currentStep && !this._validateStep(this.currentStep)) {
      return;
    }

    // Hide any existing error
    this._hideError();

    // Intercept: returning customer sees "Dein Teich" card after step 1
    if (step === 2 && this.currentStep === 1 && this.isReturningCustomer && !this._editMode) {
      this._showReturningCard();
      return;
    }

    // If moving to step 5 (loading), collect data and submit
    if (step === 5 && this.currentStep === 4) {
      this._collectData();
      this._submitAndRedirect();
    }

    // Switch panels
    this.panels.forEach(p => (p.hidden = true));
    if (this.returningPanel) this.returningPanel.hidden = true;

    // In edit mode, switch to 4-step indicator
    if (this._editMode && this.stepsReturning && this.stepsDefault) {
      this.stepsReturning.hidden = true;
      this.stepsDefault.hidden = false;
    }

    const target = this.querySelector('[data-panel="' + step + '"]');
    if (target) {
      target.hidden = false;
      this.currentStep = step;
      this._updateStepIndicator();
    }
  }

  _updateStepIndicator() {
    // Panel 5 (loading) maps to visible step 4
    const visibleStep = this.currentStep >= 5 ? 4 : this.currentStep;
    this.stepIndicators.forEach(ind => {
      const s = parseInt(ind.dataset.step, 10);
      ind.classList.toggle('is-active', s === visibleStep);
      ind.classList.toggle('is-completed', s < visibleStep);
    });
  }

  // ---- Returning Customer ----

  _showReturningCard() {
    // Hide all panels
    this.panels.forEach(p => (p.hidden = true));

    // Show returning panel (step indicator stays visible)
    if (this.returningPanel) {
      this.returningPanel.hidden = false;
      this.currentStep = 'returning';
      this._updateReturningStepIndicator(2);
    }
  }

  _updateReturningStepIndicator(activeStep) {
    this.returningStepIndicators.forEach(ind => {
      const s = parseInt(ind.dataset.stepR, 10);
      ind.classList.toggle('is-active', s === activeStep);
      ind.classList.toggle('is-completed', s < activeStep);
    });
  }

  _returningBack() {
    // Go back to step 1 (problem selection)
    this.panels.forEach(p => (p.hidden = true));
    if (this.returningPanel) this.returningPanel.hidden = true;

    const panel1 = this.querySelector('[data-panel="1"]');
    if (panel1) panel1.hidden = false;

    this.currentStep = 1;
    this._updateReturningStepIndicator(1);
  }

  _returningSubmit() {
    // Fast path: collect problem tags, use stored data, submit directly
    this.selectedTags = Array.from(
      this.querySelectorAll('[data-panel="1"] .pond-configurator__option-row.is-selected')
    ).map(el => el.dataset.tag).filter(Boolean);

    this.pondData.teichgroesse = this.customerData.teichgroesse;
    this.pondData.fische = this.customerData.fische;
    this.pondData.teichpflanzen = this.customerData.teichpflanzen;
    this.pondData.email = this.customerData.email;

    // Show loading panel
    this.panels.forEach(p => (p.hidden = true));
    if (this.returningPanel) this.returningPanel.hidden = true;

    const loadingPanel = this.querySelector('[data-panel="5"]');
    if (loadingPanel) loadingPanel.hidden = false;

    this.currentStep = 5;
    this._updateReturningStepIndicator(3);
    this._submitAndRedirect();
  }

  _returningEdit() {
    this._editMode = true;

    // Pre-fill pond size
    if (this.pondSizeInput && this.customerData.teichgroesse) {
      this.pondSizeInput.value = this.customerData.teichgroesse;
      this.pondSizeInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.pondSizeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Pre-fill fish toggle
    if (this.fishToggle) {
      this.fishToggle.checked = this.customerData.fische;
    }

    // Pre-fill plant selection
    if (this.customerData.teichpflanzen) {
      const plantBtn = this.querySelector(
        '.pond-configurator__plant-btn[data-plant="' + this.customerData.teichpflanzen + '"]'
      );
      if (plantBtn) this._selectPlant(plantBtn);
    }

    // Pre-fill email
    if (this.emailInput && this.customerData.email) {
      this.emailInput.value = this.customerData.email;
    }

    // Hide email section (returning customer already has email)
    const emailSection = this.querySelector('.pond-configurator__email-section');
    if (emailSection) emailSection.hidden = true;

    // Navigate to step 2 (normal flow)
    this._goToStep(2);
  }

  // ---- Validation ----

  _validateStep(step) {
    if (step === 1) {
      const selected = this.querySelectorAll('[data-panel="1"] .pond-configurator__option-row.is-selected');
      if (selected.length === 0) {
        this._showError(bioteigaT('error_select_problem', 'Bitte w\u00e4hlen Sie mindestens ein Problem oder einen Wasserwert aus.'));
        return false;
      }
      return true;
    }

    if (step === 2) {
      const size = parseInt(this.pondSizeInput?.value, 10);
      if (!size || size <= 0) {
        if (this.pondSizeInput) {
          this.pondSizeInput.style.borderColor = '#e53e3e';
          this.pondSizeInput.style.boxShadow = '0 0 0 3px rgba(229, 62, 62, 0.15)';
          this.pondSizeInput.addEventListener('input', () => {
            this.pondSizeInput.style.borderColor = '';
            this.pondSizeInput.style.boxShadow = '';
          }, { once: true });
        }
        return false;
      }
      return true;
    }

    if (step === 3) {
      const plantSelected = this.querySelector('.pond-configurator__plant-btn.is-selected');
      if (!plantSelected) {
        this._showError(bioteigaT('error_select_plants', 'Bitte w\u00e4hlen Sie die Menge der Teichpflanzen.'));
        return false;
      }
      return true;
    }

    if (step === 4) {
      // Returning customers already have email — skip validation
      if (this.isReturningCustomer && this.customerData?.email) {
        return true;
      }
      const email = this.emailInput;
      if (!email || !email.value || !email.validity.valid) {
        if (email) {
          email.style.borderColor = '#e53e3e';
          email.style.boxShadow = '0 0 0 3px rgba(229, 62, 62, 0.15)';
          email.addEventListener('input', () => {
            email.style.borderColor = '';
            email.style.boxShadow = '';
          }, { once: true });
        }
        return false;
      }
      return true;
    }

    return true;
  }

  _showError(message) {
    let banner = this.querySelector('.pond-configurator__error');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'pond-configurator__error';
      banner.setAttribute('role', 'alert');
      const currentPanel = this.querySelector('[data-panel="' + this.currentStep + '"]');
      if (currentPanel) {
        currentPanel.prepend(banner);
      }
    }
    banner.textContent = message;
    banner.hidden = false;
    banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _hideError() {
    const banner = this.querySelector('.pond-configurator__error');
    if (banner) banner.hidden = true;
  }

  // ---- Data Collection ----

  _collectData() {
    // Collect selected tags
    this.selectedTags = Array.from(
      this.querySelectorAll('[data-panel="1"] .pond-configurator__option-row.is-selected')
    ).map(el => el.dataset.tag).filter(Boolean);

    // Collect pond data
    this.pondData.teichgroesse = parseInt(this.pondSizeInput?.value, 10) || 0;
    this.pondData.fische = this.fishToggle?.checked ?? false;
    this.pondData.teichpflanzen = this.querySelector('.pond-configurator__plant-btn.is-selected')?.dataset.plant || '';
    this.pondData.email = this.emailInput?.value || this.customerData?.email || '';
  }

  // ---- Submit & Redirect ----

  async _submitAndRedirect() {
    const endpoint = this.dataset.workerEndpoint;

    // Save data to worker (non-blocking)
    if (endpoint) {
      try {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: this.pondData.email,
            teichgroesse: this.pondData.teichgroesse,
            fische: this.pondData.fische,
            teichpflanzen: this.pondData.teichpflanzen,
            selected_problems: this.selectedTags
          })
        });
      } catch (err) {
        // Silently fail - redirect happens regardless
        console.warn('[Konfigurator] Kundendaten konnten nicht gespeichert werden:', err);
      }
    }

    this._redirect();
  }

  _redirect() {
    const baseUrl = this.dataset.collectionUrl || '/collections/alle-produkte';
    const tags = [...this.selectedTags];

    // Add fish tag if customer has fish
    if (this.pondData.fische) {
      tags.push('kr-fische');
    }

    // Add plants tag if customer has plants (wenig or viele)
    if (this.pondData.teichpflanzen === 'wenig' || this.pondData.teichpflanzen === 'viele') {
      tags.push('kr-pflanzen');
    }

    if (tags.length > 0) {
      // OS 2.0 Search & Discovery: filter.p.tag repeated = OR
      const params = tags.map(t => 'filter.p.tag=' + encodeURIComponent(t)).join('&');
      window.location.href = baseUrl + '?' + params;
    } else {
      window.location.href = baseUrl;
    }
  }
}

// Register custom element
if (!window.customElements.get('pond-configurator')) {
  window.customElements.define('pond-configurator', PondConfigurator);
}

/**
 * Embed Mode: For placing the configurator via script tag on any page.
 *
 * Usage:
 *   <div id="bioteiga-konfigurator"
 *        data-collection-url="/collections/alle-produkte"
 *        data-worker-endpoint="https://your-worker.workers.dev/api/customer">
 *   </div>
 *   <link rel="stylesheet" href="https://cdn.shopify.com/.../bioteiga-configurator.css">
 *   <script src="https://cdn.shopify.com/.../bioteiga-configurator.js" defer></script>
 */
window.BioteigaConfigurator = {
  embedIn: function(container) {
    if (!container || container.querySelector('pond-configurator')) return;

    var collectionUrl = container.dataset.collectionUrl || '/collections/alle-produkte';
    var workerEndpoint = container.dataset.workerEndpoint || '';

    // Forward customer data attributes if present
    var customerAttrs = '';
    var customerKeys = ['customerEmail', 'customerTeichgroesse', 'customerFische', 'customerTeichpflanzen'];
    customerKeys.forEach(function(key) {
      if (container.dataset[key]) {
        var attrName = 'data-' + key.replace(/([A-Z])/g, '-$1').toLowerCase();
        customerAttrs += ' ' + attrName + '="' + container.dataset[key] + '"';
      }
    });

    var html = '<pond-configurator'
      + ' data-collection-url="' + collectionUrl + '"'
      + ' data-worker-endpoint="' + workerEndpoint + '"'
      + customerAttrs
      + '>'
      + this._buildStepIndicator(container)
      + this._buildPanel1()
      + this._buildReturningPanel(container)
      + this._buildPanel2()
      + this._buildPanel3()
      + this._buildPanel4()
      + this._buildPanel5()
      + '</pond-configurator>';

    container.innerHTML = html;
  },

  _buildStepIndicator: function(container) {
    var isReturning = !!(container && container.dataset.customerTeichgroesse);

    // 4-step default indicator
    var defaultSteps = '<div class="pond-configurator__steps" data-steps="default"' + (isReturning ? ' hidden' : '') + '>'
      + '<div class="pond-configurator__step-indicator is-active" data-step="1">'
      + '<span class="pond-configurator__step-number">1</span>'
      + '<span class="pond-configurator__step-label">' + bioteigaT('step_problem', 'Problem') + '</span></div>'
      + '<div class="pond-configurator__step-divider"></div>'
      + '<div class="pond-configurator__step-indicator" data-step="2">'
      + '<span class="pond-configurator__step-number">2</span>'
      + '<span class="pond-configurator__step-label">' + bioteigaT('step_pond_size', 'Teichgr\u00f6\u00dfe') + '</span></div>'
      + '<div class="pond-configurator__step-divider"></div>'
      + '<div class="pond-configurator__step-indicator" data-step="3">'
      + '<span class="pond-configurator__step-number">3</span>'
      + '<span class="pond-configurator__step-label">' + bioteigaT('step_pond_data', 'Teichdaten') + '</span></div>'
      + '<div class="pond-configurator__step-divider"></div>'
      + '<div class="pond-configurator__step-indicator" data-step="4">'
      + '<span class="pond-configurator__step-number">4</span>'
      + '<span class="pond-configurator__step-label">' + bioteigaT('step_result', 'Ergebnis') + '</span></div>'
      + '</div>';

    if (!isReturning) return defaultSteps;

    // 3-step returning indicator
    var returningSteps = '<div class="pond-configurator__steps" data-steps="returning">'
      + '<div class="pond-configurator__step-indicator is-active" data-step-r="1">'
      + '<span class="pond-configurator__step-number">1</span>'
      + '<span class="pond-configurator__step-label">' + bioteigaT('step_problem', 'Problem') + '</span></div>'
      + '<div class="pond-configurator__step-divider"></div>'
      + '<div class="pond-configurator__step-indicator" data-step-r="2">'
      + '<span class="pond-configurator__step-number">2</span>'
      + '<span class="pond-configurator__step-label">' + bioteigaT('step_my_pond', 'Mein Teich') + '</span></div>'
      + '<div class="pond-configurator__step-divider"></div>'
      + '<div class="pond-configurator__step-indicator" data-step-r="3">'
      + '<span class="pond-configurator__step-number">3</span>'
      + '<span class="pond-configurator__step-label">' + bioteigaT('step_result', 'Ergebnis') + '</span></div>'
      + '</div>';

    return defaultSteps + returningSteps;
  },

  _buildPanel1: function() {
    var problems = [
      { tag: 'kr-fadenalgen', icon: '\uD83C\uDF3F', title: bioteigaT('problem_filament_title', 'Fadenalgen'), sub: bioteigaT('problem_filament_sub', 'Gr\u00fcne Fadenalgen im Teich') },
      { tag: 'kr-teichschlamm', icon: '\uD83C\uDF2B\uFE0F', title: bioteigaT('problem_sludge_title', 'Teichschlamm'), sub: bioteigaT('problem_sludge_sub', 'Schlamm am Teichboden') },
      { tag: 'kr-algen', icon: '\uD83D\uDCA7', title: bioteigaT('problem_algae_title', 'Gr\u00fcnes tr\u00fcbes Wasser'), sub: bioteigaT('problem_algae_sub', 'Wasser ist gr\u00fcn und tr\u00fcb') }
    ];

    var waterValues = [
      { tag: 'kr-ph-wert', title: bioteigaT('wv_ph_label', 'pH-Wert') },
      { tag: 'kr-kh-karbonathaerte', title: 'kH' },
      { tag: 'kr-no3-nitrat', title: 'NO\u2083' },
      { tag: 'kr-po4-phosphat', title: 'PO\u2084' },
      { tag: 'kr-cl-chlor', title: 'Cl' },
      { tag: 'kr-cu-kupfer', title: 'Cu' },
      { tag: 'kr-o2-sauerstoff', title: 'O\u2082' },
      { tag: 'kr-no2-nitrit', title: 'NO\u2082' },
      { tag: 'kr-nh4-nh3-ammonium', title: 'NH\u2084/NH\u2083' },
      { tag: 'kr-gh-gesamthaerte', title: 'gH' },
      { tag: 'kr-fe-eisen', title: 'Fe' },
      { tag: 'kr-temperatur', title: '\u00b0C' }
    ];

    var html = '<div class="pond-configurator__panel" data-panel="1">';

    // Problems section (can be hidden)
    html += '<div class="pond-configurator__problems-section" data-problems-section>';
    html += '<p class="pond-configurator__section-title">' + bioteigaT('problem_title', 'Welches Problem hat Ihr Teich?') + '</p>';
    html += '<div class="pond-configurator__options pond-configurator__options--problems">';
    problems.forEach(function(p) {
      html += '<div class="pond-configurator__option-row" data-tag="' + p.tag + '" tabindex="0" role="button" aria-pressed="false">'
        + '<span class="pond-configurator__option-row-icon">' + p.icon + '</span>'
        + '<div class="pond-configurator__option-row-text">'
        + '<div class="pond-configurator__option-row-title">' + p.title + '</div>'
        + '<div class="pond-configurator__option-row-subtitle">' + p.sub + '</div>'
        + '</div>'
        + '<span class="pond-configurator__option-row-check"></span>'
        + '</div>';
    });
    html += '</div></div>';

    // Water values (hidden)
    html += '<div class="pond-configurator__water-section" hidden>';
    html += '<p class="pond-configurator__section-title">' + bioteigaT('water_title', 'Welche Wasserwerte stimmen nicht?') + '</p>';
    html += '<div class="pond-configurator__options pond-configurator__options--water-values">';
    waterValues.forEach(function(w) {
      html += '<div class="pond-configurator__option-row pond-configurator__option-row--compact" data-tag="' + w.tag + '" tabindex="0" role="button" aria-pressed="false">'
        + '<div class="pond-configurator__option-row-text">'
        + '<div class="pond-configurator__option-row-title">' + w.title + '</div>'
        + '</div>'
        + '<span class="pond-configurator__option-row-check"></span>'
        + '</div>';
    });
    html += '</div></div>';

    html += '<div class="pond-configurator__actions pond-configurator__actions--spread">'
      + '<button type="button" class="pond-configurator__water-toggle-link" data-toggle-water>' + bioteigaT('water_toggle_open', 'Wasserwerte eintragen') + '</button>'
      + '<button type="button" class="pond-configurator__water-toggle-link" data-toggle-problems hidden>' + bioteigaT('water_toggle_problems', 'Problem ausw\u00e4hlen') + '</button>'
      + '<button type="button" class="button button--xl" data-action="next">' + bioteigaT('action_next', 'Weiter') + '</button>'
      + '</div>';

    html += '</div>';
    return html;
  },

  _buildReturningPanel: function(container) {
    var teichgroesse = container.dataset.customerTeichgroesse;
    if (!teichgroesse) return '';

    var fische = container.dataset.customerFische === 'true' ? bioteigaT('yes', 'Ja') : bioteigaT('no', 'Nein');
    var plantKey = container.dataset.customerTeichpflanzen || 'keine';
    var plantLabels = {
      keine: bioteigaT('plants_none', 'Keine'),
      wenig: bioteigaT('plants_few', 'Wenig'),
      viele: bioteigaT('plants_many', 'Viele')
    };
    var pflanzen = plantLabels[plantKey] || (plantKey.charAt(0).toUpperCase() + plantKey.slice(1));

    return '<div class="pond-configurator__panel" data-panel="returning" hidden>'
      + '<div class="pond-configurator__returning-card">'
      + '<div class="pond-configurator__returning-header">'
      + '<p class="pond-configurator__returning-title">' + bioteigaT('returning_title', 'Dein Teich') + '</p>'
      + '<button type="button" class="pond-configurator__returning-edit" data-action="edit-pond" aria-label="' + bioteigaT('returning_edit', 'Teichdaten bearbeiten') + '">'
      + '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>'
      + '</button>'
      + '</div>'
      + '<div class="pond-configurator__returning-details">'
      + '<div class="pond-configurator__returning-row"><span class="pond-configurator__returning-icon">\uD83D\uDCA7</span><span class="pond-configurator__returning-label">' + bioteigaT('returning_pond_size', 'Teichgr\u00f6\u00dfe') + '</span><span class="pond-configurator__returning-value">' + parseInt(teichgroesse, 10).toLocaleString('de-DE') + ' ' + bioteigaT('unit_liter', 'Liter') + '</span></div>'
      + '<div class="pond-configurator__returning-row"><span class="pond-configurator__returning-icon">\uD83D\uDC1F</span><span class="pond-configurator__returning-label">' + bioteigaT('returning_fish', 'Fische') + '</span><span class="pond-configurator__returning-value">' + fische + '</span></div>'
      + '<div class="pond-configurator__returning-row"><span class="pond-configurator__returning-icon">\uD83C\uDF3F</span><span class="pond-configurator__returning-label">' + bioteigaT('returning_plants', 'Teichpflanzen') + '</span><span class="pond-configurator__returning-value">' + pflanzen + '</span></div>'
      + '</div>'
      + '</div>'
      + '<div class="pond-configurator__actions pond-configurator__actions--pair">'
      + '<button type="button" class="button button--outline button--xl" data-action="returning-back">' + bioteigaT('action_back', 'Zur\u00fcck') + '</button>'
      + '<button type="button" class="button button--xl" data-action="returning-submit">' + bioteigaT('action_show_solution', 'L\u00f6sung anzeigen') + '</button>'
      + '</div>'
      + '</div>';
  },

  _buildPanel2: function() {
    var html = '<div class="pond-configurator__panel" data-panel="2" hidden>';
    html += '<div class="pond-configurator__form-fields">';
    html += '<p class="pond-configurator__section-title">' + bioteigaT('size_title', 'Wie gro\u00df ist Ihr Teich?') + '</p>';

    // Pond size
    html += '<div data-pond-size-wrapper>'
      + '<label for="embed-teichgroesse" style="display:block;font-weight:600;margin-bottom:0.5rem;">' + bioteigaT('size_label', 'Teichgr\u00f6\u00dfe (Liter)') + '</label>'
      + '<input id="embed-teichgroesse" type="number" min="1" data-pond-size placeholder="' + bioteigaT('size_placeholder', 'z.B. 12000') + '" required style="width:100%;padding:0.75rem;border:1px solid rgba(0,0,0,0.2);border-radius:6px;">'
      + '</div>'
      + '<p class="pond-configurator__field-hint">' + bioteigaT('size_hint', 'Die genaue Teichgr\u00f6\u00dfe hilft uns, die richtige Dosierung zu berechnen.') + '</p>'
      + '<button type="button" data-calc-toggle class="pond-configurator__calc-toggle">' + bioteigaT('calc_toggle_open', 'Teichgr\u00f6\u00dfe berechnen') + '</button>';

    // Calculator
    html += '<div data-calculator hidden class="pond-configurator__calculator">'
      + '<p class="pond-configurator__calc-label">' + bioteigaT('shape_title', 'Welche Form hat Ihr Teich?') + '</p>'
      + '<div class="pond-configurator__shape-selector">'
      + '<button type="button" class="pond-configurator__shape-btn is-selected" data-shape="rectangular"><svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="12" width="36" height="24" rx="2"/></svg><span>' + bioteigaT('shape_rect', 'Rechteckig') + '</span></button>'
      + '<button type="button" class="pond-configurator__shape-btn" data-shape="round"><svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2"><ellipse cx="24" cy="24" rx="20" ry="14"/></svg><span>' + bioteigaT('shape_round', 'Rund') + '</span></button>'
      + '</div>'
      + '<div data-calc-fields-rectangular style="display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;">'
      + '<div><label style="display:block;font-size:0.8rem;margin-bottom:0.25rem;">' + bioteigaT('calc_length', 'L\u00e4nge (m)') + '</label><input type="number" step="0.1" data-calc-length style="width:100%;padding:0.5rem;border:1px solid rgba(0,0,0,0.2);border-radius:6px;"></div>'
      + '<div><label style="display:block;font-size:0.8rem;margin-bottom:0.25rem;">' + bioteigaT('calc_width', 'Breite (m)') + '</label><input type="number" step="0.1" data-calc-width style="width:100%;padding:0.5rem;border:1px solid rgba(0,0,0,0.2);border-radius:6px;"></div>'
      + '<div><label style="display:block;font-size:0.8rem;margin-bottom:0.25rem;">' + bioteigaT('calc_depth', 'Tiefste Stelle (m)') + '</label><input type="number" step="0.1" data-calc-depth style="width:100%;padding:0.5rem;border:1px solid rgba(0,0,0,0.2);border-radius:6px;"></div>'
      + '</div>'
      + '<div data-calc-fields-round style="display:grid;grid-template-columns:repeat(2,1fr);gap:1rem;" hidden>'
      + '<div><label style="display:block;font-size:0.8rem;margin-bottom:0.25rem;">' + bioteigaT('calc_radius', 'Radius (m)') + '</label><input type="number" step="0.1" data-calc-radius style="width:100%;padding:0.5rem;border:1px solid rgba(0,0,0,0.2);border-radius:6px;"></div>'
      + '<div><label style="display:block;font-size:0.8rem;margin-bottom:0.25rem;">' + bioteigaT('calc_depth', 'Tiefste Stelle (m)') + '</label><input type="number" step="0.1" data-calc-depth-round style="width:100%;padding:0.5rem;border:1px solid rgba(0,0,0,0.2);border-radius:6px;"></div>'
      + '</div>'
      + '<p class="pond-configurator__calc-formula" data-calc-formula>' + bioteigaT('formula_rect', 'Formel: L\u00e4nge \u00d7 Breite \u00d7 Tiefe \u00f7 1,5 \u00d7 1.000') + '</p>'
      + '<p class="pond-configurator__calc-result">' + bioteigaT('calc_result_label', 'Ergebnis:') + ' <strong data-calc-result>0 ' + bioteigaT('unit_liter', 'Liter') + '</strong></p>'
      + '<div class="pond-configurator__calc-apply"><button type="button" data-calc-apply class="button button--sm">' + bioteigaT('calc_apply', '\u00dcbernehmen') + '</button></div>'
      + '</div>';

    html += '</div>'; // form-fields

    html += '<div class="pond-configurator__actions pond-configurator__actions--pair">'
      + '<button type="button" class="button button--outline button--xl" data-action="back">' + bioteigaT('action_back', 'Zur\u00fcck') + '</button>'
      + '<button type="button" class="button button--xl" data-action="next">' + bioteigaT('action_next', 'Weiter') + '</button>'
      + '</div>';

    html += '</div>';
    return html;
  },

  _buildPanel3: function() {
    var html = '<div class="pond-configurator__panel" data-panel="3" hidden>';
    html += '<div class="pond-configurator__form-fields">';
    html += '<p class="pond-configurator__section-title">' + bioteigaT('details_title', 'Erz\u00e4hlen Sie uns mehr \u00fcber Ihren Teich') + '</p>';

    // Fish toggle
    html += '<div>'
      + '<div class="pond-configurator__field-label">\uD83D\uDC1F ' + bioteigaT('details_fish_label', 'Haben Sie Fische im Teich?') + '</div>'
      + '<div class="pond-configurator__switch-wrapper">'
      + '<span class="pond-configurator__switch-label-text">' + bioteigaT('no', 'Nein') + '</span>'
      + '<label class="pond-configurator__switch">'
      + '<input type="checkbox" data-fish-toggle class="pond-configurator__switch-input">'
      + '<span class="pond-configurator__switch-track"></span>'
      + '</label>'
      + '<span class="pond-configurator__switch-label-text">' + bioteigaT('yes', 'Ja') + '</span>'
      + '</div></div>';

    // Plants
    html += '<div>'
      + '<div class="pond-configurator__field-label">\uD83C\uDF3F ' + bioteigaT('details_plants_label', 'Wie viele Teichpflanzen haben Sie?') + '</div>'
      + '<div class="pond-configurator__plant-options">'
      + '<div class="pond-configurator__plant-btn is-selected" data-plant="keine" tabindex="0" role="button" aria-pressed="true">' + bioteigaT('plants_none', 'Keine') + '</div>'
      + '<div class="pond-configurator__plant-btn" data-plant="wenig" tabindex="0" role="button" aria-pressed="false">' + bioteigaT('plants_few', 'Wenig') + '</div>'
      + '<div class="pond-configurator__plant-btn" data-plant="viele" tabindex="0" role="button" aria-pressed="false">' + bioteigaT('plants_many', 'Viele') + '</div>'
      + '</div></div>';

    html += '</div>'; // form-fields

    html += '<div class="pond-configurator__actions pond-configurator__actions--pair">'
      + '<button type="button" class="button button--outline button--xl" data-action="back">' + bioteigaT('action_back', 'Zur\u00fcck') + '</button>'
      + '<button type="button" class="button button--xl" data-action="next">' + bioteigaT('action_next', 'Weiter') + '</button>'
      + '</div>';

    html += '</div>';
    return html;
  },

  _buildPanel4: function() {
    var html = '<div class="pond-configurator__panel" data-panel="4" hidden>';
    html += '<div class="pond-configurator__form-fields">';

    // Success message
    html += '<div class="pond-configurator__success">'
      + '<div class="pond-configurator__success-icon">\u2705</div>'
      + '<p class="pond-configurator__success-title h4">' + bioteigaT('success_title', 'Produkte gefunden') + '</p>'
      + '<p class="pond-configurator__success-subtitle">' + bioteigaT('success_subtitle', 'Wir haben passende Produkte f\u00fcr Ihr Teichproblem gefunden.') + '</p>'
      + '</div>';

    // Email
    html += '<div class="pond-configurator__email-section">'
      + '<input id="embed-email" type="email" data-email placeholder="' + bioteigaT('email_label', 'E-Mail-Adresse') + '" required style="width:100%;padding:0.75rem;border:1px solid rgba(0,0,0,0.2);border-radius:8px;">'
      + '<p class="pond-configurator__email-hint">' + bioteigaT('email_hint', 'Email eingeben um Produkte zu sehen & Vorteile zu erhalten') + '</p>'
      + '</div>';

    // Benefits grid
    html += '<div class="pond-configurator__benefits-grid">'
      + '<div class="pond-configurator__benefit-box">'
      + '<span class="pond-configurator__benefit-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg></span>'
      + '<strong class="pond-configurator__benefit-title">' + bioteigaT('benefit1_title', '5\u20ac Shop Guthaben') + '</strong>'
      + '<span class="pond-configurator__benefit-text">' + bioteigaT('benefit1_text', 'Erhalten Sie f\u00fcr Ihren n\u00e4chsten Einkauf.') + '</span>'
      + '</div>'
      + '<div class="pond-configurator__benefit-box">'
      + '<span class="pond-configurator__benefit-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></span>'
      + '<strong class="pond-configurator__benefit-title">' + bioteigaT('benefit2_title', 'Teichdaten') + '</strong>'
      + '<span class="pond-configurator__benefit-text">' + bioteigaT('benefit2_text', 'Ihre Teichdaten sind f\u00fcr den n\u00e4chsten Einkauf gesichert') + '</span>'
      + '</div>'
      + '<div class="pond-configurator__benefit-box">'
      + '<span class="pond-configurator__benefit-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg></span>'
      + '<strong class="pond-configurator__benefit-title">' + bioteigaT('benefit3_title', 'Gratis Handbuch') + '</strong>'
      + '<span class="pond-configurator__benefit-text">' + bioteigaT('benefit3_text', 'BioTeiGa Praxis-Handbuch (8,70\u20ac)') + '</span>'
      + '</div>'
      + '</div>';

    html += '</div>'; // form-fields

    html += '<div class="pond-configurator__actions pond-configurator__actions--pair">'
      + '<button type="button" class="button button--outline button--xl" data-action="back">' + bioteigaT('action_back', 'Zur\u00fcck') + '</button>'
      + '<button type="button" class="button button--xl" data-action="next">' + bioteigaT('action_show_products', 'Produkte anzeigen') + '</button>'
      + '</div>';

    html += '</div>';
    return html;
  },

  _buildPanel5: function() {
    return '<div class="pond-configurator__panel" data-panel="5" hidden>'
      + '<div class="pond-configurator__result">'
      + '<div class="pond-configurator__spinner"></div>'
      + '<p style="font-weight:700;font-size:1.125rem;">' + bioteigaT('loading_title', 'Ihre Empfehlung wird erstellt...') + '</p>'
      + '<p style="opacity:0.65;">' + bioteigaT('loading_subtitle', 'Sie werden gleich zu den passenden Produkten weitergeleitet.') + '</p>'
      + '</div></div>';
  }
};

// Auto-init for embed mode
document.addEventListener('DOMContentLoaded', function() {
  var container = document.getElementById('bioteiga-konfigurator');
  if (container && !container.querySelector('pond-configurator')) {
    window.BioteigaConfigurator.embedIn(container);
  }
});
